import React, { FC } from 'react'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { ModalSheet } from '@/atoms/ModalSheet'
import { CaptionMD, EyebrowMD, TitleSM } from '@/atoms/Text'
import { TheatricalShowtime } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { useEnhancedShowtimeContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/EnhancedShowtimeContext'
import { ShowtimesVenue } from '../../hooks/useVenueSearch/useVenueSearch'
import { SeatAvailabilityShowtimeItem } from './SeatAvailabilityShowtimeItem'
import { getSeatIcon } from './utils'

interface SeatAvailabilityDrawerProps {
  isOpen: boolean
  onClose: () => void
  venue: ShowtimesVenue
  showtimes: TheatricalShowtime[]
  onShowtimeClick: (showtime: TheatricalShowtime) => void
}

export const SeatAvailabilityDrawer: FC<SeatAvailabilityDrawerProps> = ({
  isOpen,
  onClose,
  venue,
  showtimes,
  onShowtimeClick,
}) => {
  const { t } = useTranslate('tickets')
  const { isShowtimeEnhanced } = useEnhancedShowtimeContext()

  return (
    <ModalSheet
      isOpen={isOpen}
      onClose={onClose}
      panelClasses="w-full z-[100] max-w-[504px] mx-auto flex flex-col"
      backgroundScreenColor="black"
      sheetHeight="calc(100dvh - 80px)" // nav 64px + 16px buffer
    >
      {/* Header */}
      <div className="relative flex shrink-0 items-center justify-between p-4">
        <TitleSM weight="semibold" color="core-gray-900">
          {t('seatAvailability', 'Seat Availability')}
        </TitleSM>
        <button
          onClick={onClose}
          className="flex items-center justify-center rounded-full bg-black/10 p-2 hover:bg-black/20"
          aria-label="Close"
        >
          <CloseIcon size={16} color="core-gray-700" />
        </button>
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {/* Venue Info */}
        <FlexColumn className="mb-6 gap-2">
          <TitleSM weight="bold" color="core-gray-900">
            {venue.name}
          </TitleSM>
          <CaptionMD color="core-gray-600">{venue.addressText}</CaptionMD>
        </FlexColumn>

        <FlexRow className="items-center gap-7">
          <FlexRow className="items-center gap-2">
            <Image src={getSeatIcon(true)} alt="Available Seat" width={16} height={20} />
            <EyebrowMD weight="semibold" color="core-gray-700">
              {t('available', 'Available')}
            </EyebrowMD>
          </FlexRow>
          <FlexRow className="items-center gap-2">
            <Image src={getSeatIcon(false)} alt="Unavailable Seat" width={16} height={20} />
            <EyebrowMD weight="semibold" color="core-gray-700">
              {t('unavailable', 'Unavailable')}
            </EyebrowMD>
          </FlexRow>
        </FlexRow>

        {/* Showtimes List */}
        <FlexColumn>
          {showtimes.map((showtime) => (
            <SeatAvailabilityShowtimeItem
              key={showtime.id}
              showtime={showtime}
              onClick={onShowtimeClick}
              isSellingFast={isShowtimeEnhanced(showtime.ticketingId ?? '')}
            />
          ))}
        </FlexColumn>
      </div>
    </ModalSheet>
  )
}
